import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";

import { apiPaths, vendorPath, vendorSchema, type Vendor, type VendorRequest } from "@ravnar/api-contract";

import { ApiError, apiRequest } from "./client";

// The Directory's vendors — the suppliers the business buys from. Every query key starts with "vendors", so a change
// to one can refresh the kind on its own, while the Directory's own list is a projection under "directory" and is
// invalidated beside it.
//
// There is no reader for `GET /api/vendors` here, for the reason `api/other-parties.ts` gives: the Directory lists
// parties off the projection, a page at a time, and a vendor's own page reads that one vendor. The route answers the
// whole list for the tests and for whatever asks next; a query nothing calls would only be an unused export, which
// `knip` refuses.

/** Adds a vendor. Fails with 400 when the form's values are refused and 403 for anyone but an editor. */
export function addVendor(request: VendorRequest): Promise<Vendor> {
	return apiRequest(apiPaths.vendors, vendorSchema, { method: "POST", body: request });
}

/**
 * One vendor of the organization, by id: what its page opens with, including its note. Only members with access to
 * the Directory may read it; the API answers everyone else with 403 and a vendor that is gone with 404.
 */
export const vendorQueryOptions = (vendorId: string) =>
	queryOptions({
		queryKey: ["vendors", "vendor", vendorId],
		queryFn: ({ signal }) => apiRequest(vendorPath(vendorId), vendorSchema, { signal }),
		// A refused member or a vendor that is gone will not come back on a retry; the page acts on it at once.
		retry: (failureCount, error) => failureCount < 1 && !(error instanceof ApiError && error.status < 500),
	});

/**
 * Saves a vendor's whole record, its note included. Fails with 400 when the values are refused, 403 for anyone but an
 * editor of the Directory, and 404 when the vendor is gone.
 */
export function updateVendor(vendorId: string, request: VendorRequest): Promise<Vendor> {
	return apiRequest(vendorPath(vendorId), vendorSchema, { method: "PATCH", body: request });
}

/** Removes a vendor for good; the audit log keeps what became of it. Fails with 403 and 404 as above. */
export async function removeVendor(vendorId: string): Promise<void> {
	await apiRequest(vendorPath(vendorId), z.undefined(), { method: "DELETE" });
}
